/**
 * LeaderboardWall.tsx — the third Crew tab.
 *
 * A comparison-friendly board: same crew (ASM/TL/RM) across five metrics
 * (Apps / Claims / Signed IA / Builds / Revenue) over an arbitrary
 * time window. Eight built-in ranges, role-grouped sections, tap-to-sort
 * columns.
 *
 * Semantics (Apr 30, 2026):
 *   - Signed IA = HO signed the install agreement (install_agreement_signed flip)
 *   - Builds    = build approved + on the calendar (schedule_build stage,
 *                 or build_scheduled_date populated)
 *
 * Drilldown: clicking any stat cell opens a bottom-sheet listing the specific
 * deals behind that number. Role-gated: TL/ASM/exec/super-users see any rep;
 * RMs can only see their own.
 *
 * Reuses the Crew design tokens via the parent `.crew-app` container —
 * peach + slate + orange, Archivo display fonts, shared shadow vars.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { useAuth } from "../../context/AuthContext";
import { SUPER_USER_EMAILS, MANAGER_ROLES } from "./crewAuth";
import {
  type CrewLeaderboardRow,
  type WallRange,
  type WallStat,
  type DrilldownKind,
  type DrilldownDeal,
  WALL_RANGE_LABELS,
  KIND_LABELS,
  METRIC_TO_KIND,
  type CanvasserFunnelRow,
  fetchLeaderboard,
  fetchCommitmentStreaks,
  fetchWallStats,
  fetchCanvasserFunnel,
  fetchDrilldownDeals,
  rangeFor,
} from "./crewQueries";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

// ───────────────────── helpers shared with Leaderboard ─────────────────────
const AVATAR_BG = [
  "#2a2e30",
  "#345c72",
  "#c14a1b",
  "#1e5a7a",
  "#3a7ba6",
  "#2a7a43",
  "#5a4a7a",
  "#7a3a5a",
];
function avatarColor(userId: string) {
  let h = 0;
  for (const c of userId) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_BG[h % AVATAR_BG.length]!;
}
function initialsFrom(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
function firstName(full: string) {
  return full.trim().split(/\s+/)[0] ?? full;
}
/** Render a count that may be fractional (split-RM activity is 0.5). */
function fmtCount(n: number): string {
  return Number.isInteger(n) ? n.toString() : n.toFixed(1).replace(/\.0$/, "");
}
/** "$12.4k" / "$184" — compact dollar formatter for revenue cells. */
function fmtMoney(cents: number): string {
  const dollars = cents / 100;
  if (dollars === 0) return "—";
  if (dollars >= 1_000_000)
    return `$${(dollars / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  if (dollars >= 10_000)
    return `$${Math.round(dollars / 1000)}k`;
  if (dollars >= 1000)
    return `$${(dollars / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `$${Math.round(dollars)}`;
}
/** "Apr 21 → Apr 27" date label for the window banner. */
function fmtRange(start: Date, end: Date): string {
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  // end is exclusive; show inclusive end for human readability
  const inclusiveEnd = new Date(end);
  inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
  return `${fmt(start)} → ${fmt(inclusiveEnd)}`;
}
/** "Apr 25" short date from ISO string. */
function fmtShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
/** "Waiting On Claims" from "waiting_on_claims". */
function formatStage(stage: string | null): string {
  if (!stage) return "—";
  return stage
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// ───────────────────────── role permission ─────────────────────────
/**
 * Can the viewer see the drilldown for a specific rep?
 * - TL / ASM / exec / super-user → any rep
 * - RM (restore_manager) → own data only
 */
function canViewDrilldown(
  viewerRole: string | undefined,
  viewerEmail: string | undefined,
  viewerMemberId: string | undefined,
  targetUserId: string,
): boolean {
  if (!viewerRole) return false;
  if (viewerEmail && SUPER_USER_EMAILS.has(viewerEmail.toLowerCase())) return true;
  if (MANAGER_ROLES.has(viewerRole)) return true;
  // RM can see own only
  if (viewerRole === "restore_manager") return viewerMemberId === targetUserId;
  return false;
}

// ───────────────────── role grouping ─────────────────────
type RoleKey = "asm" | "team_lead" | "restore_manager";
const ROLE_GROUPS: Array<{ id: RoleKey; label: string }> = [
  { id: "asm", label: "Area Sales Managers" },
  { id: "team_lead", label: "Team Leads" },
  { id: "restore_manager", label: "Restore Managers" },
];

// ───────────────────── sorting ─────────────────────
type SortKey =
  | "name"
  | "apps"
  | "claims"
  | "builds_scheduled"
  | "builds"
  | "revenue";

type MetricKey = "apps" | "claims" | "builds_scheduled" | "builds" | "revenue_cents";

interface WallRow {
  user_id: string;
  full_name: string;
  role: string;
  apps: number;
  claims: number;
  builds_scheduled: number;
  builds: number;
  revenue_cents: number;
  on_leave: boolean;
}

// ───────────────────── drilldown state ─────────────────────
interface DrilldownTarget {
  userId: string;
  userName: string;
  metric: MetricKey;
  kind: DrilldownKind;
}

// ───────────────────── range pill order ─────────────────────
const RANGE_ORDER: WallRange[] = [
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "this_quarter",
  "last_quarter",
  "rolling_30",
  "last_ytd",
];

// ───────────────────── component ─────────────────────
export default function LeaderboardWall() {
  const { member } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [range, setRange] = useState<WallRange>("this_week");
  const [rows, setRows] = useState<CrewLeaderboardRow[]>([]);
  const [stats, setStats] = useState<Record<string, WallStat>>({});
  const [streaks, setStreaks] = useState<Record<string, number>>({});
  const [canvasserFunnel, setCanvasserFunnel] = useState<CanvasserFunnelRow[]>([]);
  const [windowLabel, setWindowLabel] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "revenue",
    dir: "desc",
  });
  const [drilldown, setDrilldown] = useState<DrilldownTarget | null>(null);

  async function reload(r: WallRange) {
    try {
      setErr(null);
      setLoading(true);
      const [lb, w, st, cf] = await Promise.all([
        fetchLeaderboard(),
        fetchWallStats(r),
        fetchCommitmentStreaks(),
        fetchCanvasserFunnel(r),
      ]);
      setRows(lb);
      setStats(w.stats);
      setStreaks(st);
      setCanvasserFunnel(cf);
      setWindowLabel(fmtRange(w.window.start, w.window.end));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load wall");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  useRealtimeTable("crew_activity_log", () => reload(range));
  // Canvasser funnel is driven by canvas apps → deal pipeline, not crew_activity_log.
  useRealtimeTable("pending_canvas_appointments", () => reload(range));

  /** Merge leaderboard rows (names + roles) with the stats map. */
  const wallRows: WallRow[] = useMemo(() => {
    return rows
      .filter((r) =>
        ROLE_GROUPS.some((g) => g.id === (r.role as RoleKey)),
      )
      .map((r) => {
        const s = stats[r.user_id];
        return {
          user_id: r.user_id,
          full_name: r.full_name,
          role: r.role,
          apps: Number(s?.apps ?? 0),
          claims: Number(s?.claims ?? 0),
          builds_scheduled: Number(s?.builds_scheduled ?? 0),
          builds: Number(s?.builds ?? 0),
          revenue_cents: Number(s?.revenue_cents ?? 0),
          on_leave: !!r.on_leave,
        };
      })
      // On-leave reps only show when they have activity in the range (split closes, etc.).
      .filter((w) => !w.on_leave || w.apps + w.claims + w.builds_scheduled + w.builds + w.revenue_cents > 0);
  }, [rows, stats]);

  /** Sort + group by role. */
  const grouped = useMemo(() => {
    const cmp = (a: WallRow, b: WallRow) => {
      const k = sort.key;
      const av = k === "name" ? a.full_name : (a as unknown as Record<string, number>)[k]!;
      const bv = k === "name" ? b.full_name : (b as unknown as Record<string, number>)[k]!;
      if (typeof av === "string" && typeof bv === "string") {
        return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const an = av as number;
      const bn = bv as number;
      return sort.dir === "asc" ? an - bn : bn - an;
    };
    return ROLE_GROUPS.map((g) => ({
      ...g,
      rows: wallRows.filter((r) => r.role === g.id).sort(cmp),
    }));
  }, [wallRows, sort]);

  /** Totals row per group + grand total. */
  const totals = useMemo(() => {
    return grouped.map((g) =>
      g.rows.reduce(
        (t, r) => ({
          apps: t.apps + r.apps,
          claims: t.claims + r.claims,
          builds_scheduled: t.builds_scheduled + r.builds_scheduled,
          builds: t.builds + r.builds,
          revenue_cents: t.revenue_cents + r.revenue_cents,
        }),
        { apps: 0, claims: 0, builds_scheduled: 0, builds: 0, revenue_cents: 0 },
      ),
    );
  }, [grouped]);

  function toggleSort(key: SortKey) {
    setSort((cur) =>
      cur.key === key
        ? { key, dir: cur.dir === "desc" ? "asc" : "desc" }
        : { key, dir: key === "name" ? "asc" : "desc" },
    );
  }

  function openDrilldown(row: WallRow, metric: MetricKey) {
    if (!canViewDrilldown(member?.role, member?.email, member?.id, row.user_id)) return;
    setDrilldown({
      userId: row.user_id,
      userName: row.full_name,
      metric,
      kind: METRIC_TO_KIND[metric],
    });
  }

  function onNavigateToDeal(dealId: string) {
    setDrilldown(null);
    navigate(`/deal/${dealId}`, {
      state: { from: location.pathname + location.search },
    });
  }

  return (
    <div
      style={{
        background: "var(--bg-paper)",
        minHeight: "100%",
        paddingBottom: 28,
      }}
    >
      {/* ───── Header ───── */}
      <div
        style={{
          background: "var(--br-slate)",
          color: "#fff",
          padding: "16px 18px 14px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 12,
          }}
        >
          <DuckMark />
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: ".18em",
                textTransform: "uppercase",
                color: "var(--br-peach)",
                lineHeight: 1,
              }}
            >
              Bardin Roofing
            </div>
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 16,
                lineHeight: 1,
                textTransform: "uppercase",
                letterSpacing: "-0.01em",
                marginTop: 2,
              }}
            >
              Leaderboard Wall
            </div>
          </div>
          {windowLabel && !loading && (
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,.55)",
                whiteSpace: "nowrap",
              }}
            >
              {windowLabel}
            </div>
          )}
        </div>

        {/* Range pill row (horizontally scrollable on small screens) */}
        <div
          style={{
            display: "flex",
            gap: 6,
            overflowX: "auto",
            paddingBottom: 2,
            scrollbarWidth: "none",
          }}
        >
          {RANGE_ORDER.map((id) => {
            const active = range === id;
            return (
              <button
                key={id}
                onClick={() => setRange(id)}
                style={{
                  flexShrink: 0,
                  padding: "6px 11px",
                  border: 0,
                  cursor: "pointer",
                  borderRadius: 99,
                  background: active ? "var(--br-orange)" : "rgba(255,255,255,.08)",
                  color: active ? "#fff" : "rgba(255,255,255,.7)",
                  fontFamily: "var(--font-sans)",
                  fontWeight: 800,
                  fontSize: 10,
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                {WALL_RANGE_LABELS[id]}
              </button>
            );
          })}
        </div>
      </div>

      {err && <ErrorCard message={err} onRetry={() => reload(range)} />}
      {loading && rows.length === 0 && <LoadingCard />}

      {/* ───── Role-grouped sections ───── */}
      {!loading &&
        !err &&
        grouped.map((g, gi) => (
          <div key={g.id} style={{ padding: "20px 14px 0" }}>
            <SectionHeader
              eyebrow={g.label}
              title={`${g.rows.length} reps`}
              right={
                <TotalsPill
                  revenue_cents={totals[gi]!.revenue_cents}
                  builds={totals[gi]!.builds}
                />
              }
            />
            <div
              style={{
                background: "#fff",
                border: "1px solid var(--line)",
                borderRadius: 14,
                overflow: "hidden",
                boxShadow: "var(--shadow-sm)",
              }}
            >
              <ColumnHeader sort={sort} onSort={toggleSort} />
              {g.rows.map((r, i) => (
                <WallRowCard
                  key={r.user_id}
                  r={r}
                  rank={i + 1}
                  streak={streaks[r.user_id] ?? 0}
                  isLast={i === g.rows.length - 1}
                  canDrilldown={(metric) =>
                    canViewDrilldown(member?.role, member?.email, member?.id, r.user_id) &&
                    (r as unknown as Record<string, number>)[metric] > 0
                  }
                  onDrilldown={(metric) => openDrilldown(r, metric)}
                />
              ))}
              {g.rows.length > 0 && <TotalsRow t={totals[gi]!} />}
              {g.rows.length === 0 && (
                <div
                  style={{
                    padding: "16px 14px",
                    color: "var(--fg3)",
                    fontSize: 12,
                    textAlign: "center",
                  }}
                >
                  No active {g.label.toLowerCase()}.
                </div>
              )}
            </div>
          </div>
        ))}

      {/* ───── Canvassers section (own app funnel, not RM-attributed) ───── */}
      {!loading && !err && (
        <CanvasserWallSection funnel={canvasserFunnel} />
      )}

      {/* ───── Drilldown modal ───── */}
      {drilldown && (
        <DrilldownModal
          target={drilldown}
          range={range}
          onClose={() => setDrilldown(null)}
          onNavigate={onNavigateToDeal}
        />
      )}
    </div>
  );
}

// ───────────────────── sub-components ─────────────────────

function ColumnHeader({
  sort,
  onSort,
}: {
  sort: { key: SortKey; dir: "asc" | "desc" };
  onSort: (k: SortKey) => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "32px 1fr repeat(5, 50px)",
        alignItems: "center",
        gap: 4,
        padding: "8px 10px",
        background: "var(--br-sky)",
        borderBottom: "1px solid var(--line)",
      }}
    >
      <div />
      <SortHeader label="Rep" k="name" sort={sort} onSort={onSort} align="left" />
      <SortHeader label="App" k="apps" sort={sort} onSort={onSort} />
      <SortHeader label="Clm" k="claims" sort={sort} onSort={onSort} />
      <SortHeader label="IA" k="builds_scheduled" sort={sort} onSort={onSort} />
      <SortHeader label="Bld" k="builds" sort={sort} onSort={onSort} />
      <SortHeader label="Rev" k="revenue" sort={sort} onSort={onSort} />
    </div>
  );
}

function SortHeader({
  label,
  k,
  sort,
  onSort,
  align = "center",
}: {
  label: string;
  k: SortKey;
  sort: { key: SortKey; dir: "asc" | "desc" };
  onSort: (k: SortKey) => void;
  align?: "left" | "center";
}) {
  const active = sort.key === k;
  return (
    <button
      type="button"
      onClick={() => onSort(k)}
      style={{
        all: "unset",
        cursor: "pointer",
        textAlign: align,
        fontFamily: "var(--font-sans)",
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: ".14em",
        textTransform: "uppercase",
        color: active ? "var(--br-orange)" : "var(--fg3)",
        padding: "2px 0",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: align === "center" ? "center" : "flex-start",
        gap: 3,
      }}
    >
      {label}
      {active && (
        <span style={{ fontSize: 8, lineHeight: 1 }}>
          {sort.dir === "desc" ? "▼" : "▲"}
        </span>
      )}
    </button>
  );
}

function WallRowCard({
  r,
  rank,
  streak = 0,
  isLast,
  canDrilldown,
  onDrilldown,
}: {
  r: WallRow;
  rank: number;
  streak?: number;
  isLast: boolean;
  canDrilldown: (metric: MetricKey) => boolean;
  onDrilldown: (metric: MetricKey) => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "32px 1fr repeat(5, 50px)",
        alignItems: "center",
        gap: 4,
        padding: "10px 10px",
        borderBottom: isLast ? "none" : "1px solid var(--line)",
      }}
    >
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: avatarColor(r.user_id),
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "var(--font-display)",
          fontSize: 9,
          letterSpacing: "-0.02em",
        }}
      >
        {initialsFrom(r.full_name)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 800,
            fontSize: 12,
            color: "var(--fg1)",
            lineHeight: 1.1,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "0 1 auto" }}>{r.full_name}</span>
          {streak > 0 && (
            <span
              title={`${streak}-week commitment streak (≥80% match)`}
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: "#9a3412",
                background: "linear-gradient(90deg, #ffedd5 0%, #fef3c7 100%)",
                padding: "1px 4px",
                borderRadius: 999,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              🔥{streak}
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: ".08em",
            textTransform: "uppercase",
            color: "var(--fg3)",
            marginTop: 2,
          }}
        >
          #{rank} · {firstName(r.full_name)}
        </div>
      </div>
      <DrillCell
        value={r.apps}
        display={r.apps > 0 ? fmtCount(r.apps) : "—"}
        clickable={canDrilldown("apps")}
        onClick={() => onDrilldown("apps")}
      />
      <DrillCell
        value={r.claims}
        display={r.claims > 0 ? fmtCount(r.claims) : "—"}
        clickable={canDrilldown("claims")}
        onClick={() => onDrilldown("claims")}
      />
      <DrillCell
        value={r.builds_scheduled}
        display={r.builds_scheduled > 0 ? fmtCount(r.builds_scheduled) : "—"}
        clickable={canDrilldown("builds_scheduled")}
        onClick={() => onDrilldown("builds_scheduled")}
      />
      <DrillCell
        value={r.builds}
        display={r.builds > 0 ? fmtCount(r.builds) : "—"}
        clickable={canDrilldown("builds")}
        onClick={() => onDrilldown("builds")}
      />
      <DrillCell
        value={r.revenue_cents}
        display={fmtMoney(r.revenue_cents)}
        clickable={canDrilldown("revenue_cents")}
        onClick={() => onDrilldown("revenue_cents")}
        isRevenue
      />
    </div>
  );
}

/** A stat cell that turns into a tappable button when canDrilldown. */
function DrillCell({
  value,
  display,
  clickable,
  onClick,
  isRevenue = false,
}: {
  value: number;
  display: string;
  clickable: boolean;
  onClick: () => void;
  isRevenue?: boolean;
}) {
  const color = isRevenue
    ? value > 0 ? "var(--br-orange-ink)" : "var(--fg3)"
    : value > 0 ? "var(--fg1)" : "var(--fg3)";

  if (!clickable) {
    return (
      <div
        style={{
          textAlign: "center",
          fontFamily: "var(--font-display)",
          fontSize: isRevenue ? 12 : 14,
          letterSpacing: "-0.02em",
          color,
        }}
      >
        {display}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title="Tap to see deals"
      style={{
        all: "unset",
        display: "block",
        width: "100%",
        textAlign: "center",
        fontFamily: "var(--font-display)",
        fontSize: isRevenue ? 12 : 14,
        letterSpacing: "-0.02em",
        color: value > 0 ? "var(--br-orange)" : "var(--fg3)",
        cursor: value > 0 ? "pointer" : "default",
        borderRadius: 6,
        padding: "3px 0",
        // subtle underline on clickable non-zero values
        textDecoration: value > 0 ? "underline" : "none",
        textDecorationStyle: "dotted",
        textUnderlineOffset: 3,
      }}
    >
      {display}
    </button>
  );
}

function TotalsRow({
  t,
}: {
  t: {
    apps: number;
    claims: number;
    builds_scheduled: number;
    builds: number;
    revenue_cents: number;
  };
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "32px 1fr repeat(5, 50px)",
        alignItems: "center",
        gap: 4,
        padding: "10px 10px",
        background: "var(--br-sky)",
        borderTop: "1px solid var(--line)",
      }}
    >
      <div />
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: ".14em",
          textTransform: "uppercase",
          color: "var(--br-orange)",
        }}
      >
        Group total
      </div>
      <NumCell n={t.apps} bold />
      <NumCell n={t.claims} bold />
      <NumCell n={t.builds_scheduled} bold />
      <NumCell n={t.builds} bold />
      <RevCell cents={t.revenue_cents} bold />
    </div>
  );
}

function NumCell({ n, bold }: { n: number; bold?: boolean }) {
  return (
    <div
      style={{
        textAlign: "center",
        fontFamily: "var(--font-display)",
        fontSize: bold ? 15 : 14,
        letterSpacing: "-0.02em",
        color: n > 0 ? "var(--fg1)" : "var(--fg3)",
        fontWeight: bold ? 700 : 400,
      }}
    >
      {n > 0 ? fmtCount(n) : "—"}
    </div>
  );
}

function RevCell({ cents, bold }: { cents: number; bold?: boolean }) {
  return (
    <div
      style={{
        textAlign: "center",
        fontFamily: "var(--font-display)",
        fontSize: bold ? 13 : 12,
        letterSpacing: "-0.02em",
        color: cents > 0 ? "var(--br-orange-ink)" : "var(--fg3)",
        fontWeight: bold ? 700 : 400,
      }}
    >
      {fmtMoney(cents)}
    </div>
  );
}

function TotalsPill({
  revenue_cents,
  builds,
}: {
  revenue_cents: number;
  builds: number;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        background: "var(--br-slate)",
        color: "#fff",
        borderRadius: 99,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: ".08em",
        textTransform: "uppercase",
      }}
    >
      <span>{fmtCount(builds)} blds</span>
      <span style={{ color: "var(--br-peach)" }}>·</span>
      <span>{fmtMoney(revenue_cents)}</span>
    </div>
  );
}

function SectionHeader({
  eyebrow,
  title,
  right,
}: {
  eyebrow?: string;
  title: string;
  right?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        marginBottom: 10,
        gap: 8,
      }}
    >
      <div>
        {eyebrow && (
          <div
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: ".16em",
              textTransform: "uppercase",
              color: "var(--br-orange)",
              marginBottom: 2,
            }}
          >
            {eyebrow}
          </div>
        )}
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 18,
            textTransform: "uppercase",
            letterSpacing: "-0.01em",
            color: "var(--fg1)",
            lineHeight: 1,
          }}
        >
          {title}
        </div>
      </div>
      {right}
    </div>
  );
}

function DuckMark() {
  return (
    <img
      src="/img/duck2.png"
      alt="Bardin Roofing"
      width={32}
      height={32}
      style={{ width: 32, height: 32, objectFit: "contain", flexShrink: 0 }}
    />
  );
}

function LoadingCard() {
  return (
    <div
      style={{
        margin: "18px 18px 0",
        padding: "24px 18px",
        background: "#fff",
        border: "1px solid var(--line)",
        borderRadius: 14,
        color: "var(--fg3)",
        fontSize: 13,
        textAlign: "center",
      }}
    >
      Adding it up…
    </div>
  );
}

function ErrorCard({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      style={{
        margin: "18px 18px 0",
        padding: "14px 16px",
        background: "#fff",
        border: "1px solid #f1b8a3",
        borderRadius: 14,
        color: "var(--br-orange-ink)",
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 13 }}>
        Couldn't load the wall.
      </div>
      <div style={{ fontSize: 12, color: "var(--fg2)", marginTop: 4 }}>
        {message}
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: 8,
          padding: "6px 10px",
          border: 0,
          background: "var(--br-orange)",
          color: "#fff",
          borderRadius: 8,
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: ".08em",
          textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        Retry
      </button>
    </div>
  );
}

// ───────────────────── DrilldownModal ─────────────────────

function DrilldownModal({
  target,
  range,
  onClose,
  onNavigate,
}: {
  target: DrilldownTarget;
  range: WallRange;
  onClose: () => void;
  onNavigate: (dealId: string) => void;
}) {
  const [deals, setDeals] = useState<DrilldownDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setErr(null);
        const { start, end } = rangeFor(range);
        const results = await fetchDrilldownDeals(
          target.userId,
          target.kind,
          start.toISOString(),
          end.toISOString(),
        );
        setDeals(results);
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Failed to load deals");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [target.userId, target.kind, range]);

  const isRevenue = target.kind === "revenue";
  const rangeLabel = WALL_RANGE_LABELS[range];
  const kindLabel = KIND_LABELS[target.kind];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,.45)",
          zIndex: 200,
        }}
      />

      {/* Sheet */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 201,
          background: "#fff",
          borderRadius: "18px 18px 0 0",
          maxHeight: "78vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 -4px 32px rgba(0,0,0,.18)",
        }}
      >
        {/* Sheet header */}
        <div
          style={{
            background: "var(--br-slate)",
            color: "#fff",
            padding: "14px 16px 12px",
            borderRadius: "18px 18px 0 0",
            flexShrink: 0,
          }}
        >
          {/* Drag pill */}
          <div
            style={{
              width: 36,
              height: 4,
              background: "rgba(255,255,255,.25)",
              borderRadius: 2,
              margin: "0 auto 12px",
            }}
          />
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                background: avatarColor(target.userId),
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--font-display)",
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              {initialsFrom(target.userName)}
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 15,
                  textTransform: "uppercase",
                  letterSpacing: "-0.01em",
                  lineHeight: 1,
                }}
              >
                {firstName(target.userName)} · {kindLabel}
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: ".1em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,.55)",
                  marginTop: 3,
                }}
              >
                {rangeLabel}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                all: "unset",
                cursor: "pointer",
                color: "rgba(255,255,255,.6)",
                fontSize: 20,
                lineHeight: 1,
                padding: "2px 6px",
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Scrollable deal list */}
        <div style={{ overflowY: "auto", flex: 1, padding: "6px 0 24px" }}>
          {loading && (
            <div
              style={{
                padding: "28px 16px",
                textAlign: "center",
                color: "var(--fg3)",
                fontSize: 13,
              }}
            >
              Loading deals…
            </div>
          )}

          {!loading && err && (
            <div
              style={{
                padding: "16px",
                color: "var(--br-orange-ink)",
                fontSize: 13,
                textAlign: "center",
              }}
            >
              {err}
            </div>
          )}

          {!loading && !err && deals.length === 0 && (
            <div
              style={{
                padding: "28px 16px",
                textAlign: "center",
                color: "var(--fg3)",
                fontSize: 13,
              }}
            >
              No deals found for this period.
            </div>
          )}

          {!loading && !err && deals.length > 0 && (
            <>
              {/* Count badge */}
              <div
                style={{
                  padding: "8px 16px 4px",
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: ".12em",
                  textTransform: "uppercase",
                  color: "var(--fg3)",
                }}
              >
                {deals.length} deal{deals.length !== 1 ? "s" : ""}
                {isRevenue && (
                  <span style={{ marginLeft: 6, color: "var(--br-orange)" }}>
                    · {fmtMoney(deals.reduce((s, d) => s + d.value, 0))} total
                  </span>
                )}
              </div>

              {deals.map((d, i) => (
                <DrilldownDealRow
                  key={d.activity_id}
                  deal={d}
                  isLast={i === deals.length - 1}
                  isRevenue={isRevenue}
                  onClick={() => onNavigate(d.deal_id)}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function DrilldownDealRow({
  deal,
  isLast,
  isRevenue,
  onClick,
}: {
  deal: DrilldownDeal;
  isLast: boolean;
  isRevenue: boolean;
  onClick: () => void;
}) {
  const isSplit = deal.value < (isRevenue ? Infinity : 1);
  const address = [deal.property_street, deal.property_city]
    .filter(Boolean)
    .join(", ");

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: "unset",
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "11px 16px",
        borderBottom: isLast ? "none" : "1px solid var(--line)",
        cursor: "pointer",
        boxSizing: "border-box",
      }}
    >
      {/* House icon */}
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          background: "var(--br-sky)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        🏠
      </div>

      {/* Deal info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 800,
            fontSize: 13,
            color: "var(--fg1)",
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {deal.homeowner_name ?? "Unknown Homeowner"}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--fg3)",
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {isAddressLinkable(deal) ? (
            <a
              href={mapsHref(deal)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              {address}
            </a>
          ) : (
            address || "No address"
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
          <StagePill stage={deal.stage} />
          {isSplit && !isRevenue && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                color: "var(--fg3)",
                background: "var(--bg-paper)",
                border: "1px solid var(--line)",
                borderRadius: 99,
                padding: "1px 5px",
              }}
            >
              ½ split
            </span>
          )}
        </div>
      </div>

      {/* Right side: date + revenue */}
      <div
        style={{
          flexShrink: 0,
          textAlign: "right",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 3,
        }}
      >
        {isRevenue && deal.value > 0 && (
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 14,
              color: "var(--br-orange-ink)",
              letterSpacing: "-0.02em",
            }}
          >
            {fmtMoney(deal.value)}
          </div>
        )}
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: "var(--fg3)",
            letterSpacing: ".04em",
          }}
        >
          {fmtShortDate(deal.occurred_at)}
        </div>
        <div style={{ color: "var(--fg3)", fontSize: 12 }}>›</div>
      </div>
    </button>
  );
}

function StagePill({ stage }: { stage: string | null }) {
  // Color-code a handful of key stages; fall back to neutral.
  const stageStr = stage ?? "";
  let bg = "var(--bg-paper)";
  let color = "var(--fg3)";
  let border = "1px solid var(--line)";

  if (stageStr.includes("complete") || stageStr.includes("closed")) {
    bg = "#d1fae5"; color = "#065f46"; border = "1px solid #6ee7b7";
  } else if (stageStr.includes("waiting") || stageStr.includes("claim")) {
    bg = "#fef3c7"; color = "#92400e"; border = "1px solid #fcd34d";
  } else if (stageStr.includes("appointment") || stageStr.includes("received")) {
    bg = "#dbeafe"; color = "#1e40af"; border = "1px solid #93c5fd";
  } else if (stageStr.includes("schedule") || stageStr.includes("build")) {
    bg = "#ede9fe"; color = "#5b21b6"; border = "1px solid #c4b5fd";
  } else if (stageStr.includes("lost") || stageStr.includes("cancel")) {
    bg = "#fee2e2"; color = "#991b1b"; border = "1px solid #fca5a5";
  }

  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: ".06em",
        textTransform: "uppercase",
        background: bg,
        color,
        border,
        borderRadius: 99,
        padding: "2px 6px",
        lineHeight: 1.4,
        display: "inline-block",
        maxWidth: 120,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {formatStage(stage)}
    </span>
  );
}

// ───────────────────── Canvassers section (Ship 4) ─────────────────────
// A canvasser's OWN approved apps walked through the pipeline. Distinct from the
// RM/TL/RM sections above (those credit the deal owner). Sourced from the
// canvasser_funnel RPC. Spec: memory/features/canvasser-program.md
function CanvasserWallSection({ funnel }: { funnel: CanvasserFunnelRow[] }) {
  const rows = useMemo(
    () => [...funnel].sort((a, b) => b.apps - a.apps || b.closed_won - a.closed_won),
    [funnel],
  );
  const totals = rows.reduce(
    (t, r) => ({
      apps: t.apps + r.apps,
      claims: t.claims + r.claims,
      builds_scheduled: t.builds_scheduled + r.builds_scheduled,
      builds: t.builds + r.builds,
      closed_won: t.closed_won + r.closed_won,
    }),
    { apps: 0, claims: 0, builds_scheduled: 0, builds: 0, closed_won: 0 },
  );
  const cols: Array<{ key: keyof typeof totals; label: string }> = [
    { key: "apps", label: "Apps" },
    { key: "claims", label: "Claims" },
    { key: "builds_scheduled", label: "B-Sched" },
    { key: "builds", label: "Builds" },
    { key: "closed_won", label: "Closed" },
  ];
  const cell: React.CSSProperties = { width: 58, textAlign: "right", fontVariantNumeric: "tabular-nums" };
  const builtPct = (r: { apps: number; builds: number }) =>
    r.apps > 0 ? Math.round((r.builds / r.apps) * 100) : 0;

  return (
    <div style={{ padding: "20px 14px 0" }}>
      <SectionHeader
        eyebrow="Canvassers"
        title={`${rows.length} ${rows.length === 1 ? "canvasser" : "canvassers"}`}
        right={
          <span style={{ fontSize: 12, color: "var(--fg3)" }}>
            {totals.apps} apps → {totals.builds} builds
          </span>
        }
      />
      <div
        style={{
          background: "#fff",
          border: "1px solid var(--line)",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "var(--shadow-sm)",
        }}
      >
        {/* header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
            borderBottom: "1px solid var(--line)",
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            color: "var(--fg3)",
          }}
        >
          <span style={{ flex: 1 }}>Rep</span>
          {cols.map((c) => (
            <span key={c.key} style={cell}>{c.label}</span>
          ))}
          <span style={{ ...cell, width: 64 }}>→Build%</span>
        </div>
        {rows.length === 0 ? (
          <div style={{ padding: "16px 14px", color: "var(--fg3)", fontSize: 12, textAlign: "center" }}>
            No canvasser apps in this window.
          </div>
        ) : (
          rows.map((r, i) => (
            <div
              key={r.canvasser_id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                borderBottom: i === rows.length - 1 ? "none" : "1px solid var(--line)",
                fontSize: 13,
              }}
            >
              <span style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span
                  aria-hidden
                  style={{
                    width: 24, height: 24, borderRadius: 999, flexShrink: 0,
                    background: avatarColor(r.canvasser_id), color: "#fff",
                    fontSize: 10, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {initialsFrom(r.full_name)}
                </span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.full_name}
                </span>
              </span>
              {cols.map((c) => (
                <span key={c.key} style={{ ...cell, fontWeight: c.key === "apps" ? 700 : 400 }}>
                  {fmtCount(r[c.key])}
                </span>
              ))}
              <span style={{ ...cell, width: 64, color: "var(--fg3)" }}>{builtPct(r)}%</span>
            </div>
          ))
        )}
        {rows.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              borderTop: "2px solid var(--line)",
              fontSize: 13,
              fontWeight: 700,
              background: "#faf7f5",
            }}
          >
            <span style={{ flex: 1 }}>Total</span>
            {cols.map((c) => (
              <span key={c.key} style={cell}>{fmtCount(totals[c.key])}</span>
            ))}
            <span style={{ ...cell, width: 64, color: "var(--fg3)" }}>
              {totals.apps > 0 ? Math.round((totals.builds / totals.apps) * 100) : 0}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
